import { useAppStore } from "@hooks/useAppStore";
import useCache from "@hooks/useCache";
import useCacheKey from "@hooks/useCacheKey";
import { useCallback } from "react";

export type ReviewCacheData = {
	lastShownPromptDate: number | null;
	lastAppVersion: string | null;
	userScore: number | null;
	hasLeftReview: boolean;
	hasCouldBeBetter: boolean;
	hasSaidMaybeLater: boolean;
};

const defaultReviewData: ReviewCacheData = {
	lastShownPromptDate: null,
	lastAppVersion: null,
	userScore: null,
	hasLeftReview: false,
	hasCouldBeBetter: false,
	hasSaidMaybeLater: false,
};

export const useReviewCache = ({
	isRoutedFromSession,
	shouldInvalidate = true,
}: {
	isRoutedFromSession: boolean;
	shouldInvalidate?: boolean;
}) => {
	const { reviewPromptCacheKey } = useCacheKey();
	const currentAppVersion = useAppStore((state) => state.app.version);
	const { data, isLoading, error, setCache, isRefetching } =
		useCache<ReviewCacheData>({
			key: reviewPromptCacheKey || "",
			defaultData: defaultReviewData,
			enabled: !!reviewPromptCacheKey && isRoutedFromSession,
			shouldInvalidate,
		});

	const currentData = data || defaultReviewData;

	return {
		...currentData,
		isLoading: isLoading || isRefetching,
		error,
		trackAppReviewCriteria: useCallback(
			(score: number) => {
				setCache({ userScore: score });
			},
			[setCache],
		),
		resetTrackedAppReviewCritieria: () => {
			setCache({
				lastShownPromptDate: Date.now(),
				lastAppVersion: currentAppVersion,
			});
		},
		resetSessionCriteriaOnly: () => {
			setCache({ userScore: null });
		},
		updateLastAppVersion: (lastAppVersion: string) => {
			setCache({ lastAppVersion });
		},
		markReviewLeft: () => {
			setCache({
				hasLeftReview: true,
				lastShownPromptDate: Date.now(),
				lastAppVersion: currentAppVersion,
			});
		},
		markCouldBeBetter: () => {
			setCache({
				hasCouldBeBetter: true,
				lastShownPromptDate: Date.now(),
				lastAppVersion: currentAppVersion,
			});
		},
		markSaidMaybeLater: () => {
			setCache({
				hasSaidMaybeLater: true,
				lastShownPromptDate: Date.now(),
				lastAppVersion: currentAppVersion,
			});
		},
		resetUserInteractions: () => {
			setCache({
				userScore: null,
				hasLeftReview: false,
				hasCouldBeBetter: false,
				hasSaidMaybeLater: false,
				lastShownPromptDate: null,
				lastAppVersion: null,
			});
		},
	};
};
